/****************************************************
 * Module Name:  lotDetailModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import LotCreationProxy from "../../../utility/proxy/manager/manufacturingProxy/lotCreationProxy";
import Constants from "../../../utility/constants";
import Utility from "../../../utility/utility";
import renderCellExpand from "../../common/dataGridCellExpand";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import { Modal } from "react-bootstrap";
import CommonSnackbar from "../../common/commonSnackBar";
import logo from '../../../images/pansuriya.jpg'
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

const LotDetailModal = ({ lotid, handleClose }) => {

    const [data, setData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {
        setLoader(true);
        LotCreationProxy.GetLotDetailsByLotId(lotid, response => {
            if (response) {

                const uniqueFilterValues = {
                    username: Utility.getDistinctOptions(response, "username")
                };

                setFilterValues(uniqueFilterValues);
                setLoader(false);

                let prevRow = null;
                response = response.sort((a, b) => b.id - a.id)
                    .map(iRow => {
                        if (prevRow == null) {
                            prevRow = iRow;
                            // Considering the 1st row as the previous row, 
                            // we wont be showing any color change in this
                            iRow.prevRow = iRow;
                        } else {
                            iRow.prevRow = prevRow;
                            prevRow = iRow;
                        }
                        return iRow;
                    })

                setData(response);
            } else {
                setLoader(false);
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
            }
        })
    }

    const genericCellValueWithColorClass = (currVal, prevVal) => {
        if (!currVal || (typeof currVal === "string" && currVal.trim() === ''))
            currVal = '';
        if (!prevVal || (typeof prevVal === "string" && prevVal.trim() === ''))
            prevVal = '';

        return currVal !== prevVal ? 'blueCell' : ""
    }

    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    const initialColumns = () => [
        {
            field: "lotcreationid",
            headerName: "LOT ID",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: 'number',
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.lotcreationid, params.row.prevRow.lotcreationid)
        },
        {
            field: "lotintimationid",
            headerName: "LOT INT ID",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: 'number',
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.lotintimationid, params.row.prevRow.lotintimationid)
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "date",
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            renderCell: (params) => params.value ? Utility.ConvertUtcToLocalTime(params.value) : " - ",
            cellClassName: (params) => genericCellValueWithColorClass(params.row.createdat, params.row.prevRow.createdat)
        },
        {
            field: "datereceived",
            headerName: "RECEIVED AT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "date",
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            renderCell: (params) => params.value ? Utility.ConvertUtcToLocalTime(params.value) : " - ",
            cellClassName: (params) => genericCellValueWithColorClass(params.row.datereceived, params.row.prevRow.datereceived)
        },
        {
            field: "updatedat",
            headerName: "UPDATED AT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "date",
            valueGetter: (params) => params.value ? new Date(params.value) : null,
            renderCell: (params) => params.value ? Utility.ConvertUtcToLocalTime(params.value) : " - ",
            cellClassName: (params) => genericCellValueWithColorClass(params.row.updatedat, params.row.prevRow.updatedat)
        },
        {
            field: "carats",
            headerName: "INITIAL CTS",
            headerAlign: "center",
            align: "center",
            width: 115,
            type: 'number',
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.carats),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.carats, params.row.prevRow.carats)
        },
        {
            field: "price",
            headerName: "INITIAL PPC",
            headerAlign: "center",
            align: "center",
            type: 'number',
            width: 120,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.price, params.row.prevRow.price)
        },
        {
            field: "pendingcarats",
            headerName: "PENDING CTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            type: 'number',
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.pendingcarats),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.pendingcarats, params.row.prevRow.pendingcarats)
        },
        {
            field: "pendingprice",
            headerName: "PENDING PPC",
            headerAlign: "center",
            align: "center",
            width: 200,
            type: 'number',
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.pendingprice),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.pendingprice, params.row.prevRow.pendingprice)
        },
        {
            field: "username",
            headerName: "USERNAME",
            headerAlign: "center",
            align: "center",
            width: 280,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            valueOptions: filterValues && filterValues.username ? filterValues.username : [],
            cellClassName: (params) => genericCellValueWithColorClass(params.row.username, params.row.prevRow.username)
        },
    ]
    const columns = initialColumns();

    return (
        <Box>
            <Modal backdrop="static" show={true} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>

                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Error" : "Warning"
                    }
                    handleClose={() => setShowSnackbar(false)}
                />

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Lot Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '70vh',
                            "& .MuiDataGrid-toolbarContainer": {
                                padding: "4px 4px 0 90px !important"
                            },
                            "& .blueCell": {
                                color: "#080808",
                                backgroundColor: "#d4eff8",
                            },
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "8px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "15px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "22px",
                                },
                                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                    width: "0.4em",
                                    height: "0.7em",
                                },
                                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                    background: "#f1f1f1",
                                },
                                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#888",
                                },
                                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                    background: "#555",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: GridToolbar }}
                            rows={data}
                            columns={columns}
                            loading={loader}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        // locationid: false,
                                        // id: false,
                                    },
                                },
                                // sorting: {
                                //     sortModel: [{ field: "updatedat", sort: "desc" }],
                                // },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />

                        <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                            {loader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer", mr: 1 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

export default LotDetailModal;